import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import {
	MessageContextProvider,
	type MessageContextData,
	useMessageContext,
} from '@atlassian/post-office-context/message-context';
import { useStableObject } from '@atlassian/post-office-context/use-stable-object';
import type {
	PlacementLocation,
	PlacementMessage,
} from '@post-office/message-template-contracts/placement-v2';
import type { TriggerType } from '@post-office/message-template-contracts/types';
import { useWaitForTrigger } from '@post-office/placement-contexts/hooks/use-wait-for-trigger';
import { MessageRendererProvider } from '@post-office/placement-contexts/message-renderer-provider';
import { MessageViewed } from '@post-office/placement-contexts/message-viewed-component';
import { isClientTriggeredMessage } from '@post-office/placement-contexts/utils/is-client-triggered-message';
import { type PropsWithChildren, useEffect, useMemo } from 'react';

import { createPlacementComponentV2 } from './create-placement-component';
import { PlacementMessageError, createPlacementMessageError } from './error';
import type {
	AnyFrontendStages,
	AnyProps,
	AnyValidatedFrontendStages,
	CreateFrontendPlacementMessageExecuteConfig,
	CreateFrontendPlacementMessageRunner,
	DefaultFrontendStages,
	NoProps,
	PlacementBrowserConfig,
	PlacementMessageFrontendFactory,
	PlacementRenderer,
} from './types';

export const createPlacementFrontend =
	<T extends AnyFrontendStages = DefaultFrontendStages>(placementConfig: PlacementBrowserConfig) =>
	<U extends AnyProps = NoProps>(
		placementRenderer: PlacementRenderer<T, U>,
	): PlacementMessageFrontendFactory<T, U> => ({
		createFrontendStage: (stage) => stage,

		createMessageFrontend: (stage) => (config) => [config.messageTemplateId, stage],

		createPlacementFrontendComponent: (config) => {
			const messageTemplateRouter = createFrontendPlacementMessageTemplateRouter({
				...placementConfig,
				...config,
			});

			return createPlacementComponentV2(
				placementConfig.placementId,
				placementRenderer(messageTemplateRouter),
				placementConfig.options,
			);
		},
	});

// The published renderer omits the category from its context; retain its other fields.
const MessageCategoryProvider = ({
	messageCategory,
	children,
}: PropsWithChildren<Pick<MessageContextData, 'messageCategory'>>) => {
	const messageContext = useMessageContext();
	const value = useMemo(
		() => ({ ...messageContext, messageCategory }),
		[messageContext, messageCategory],
	);

	return <MessageContextProvider value={value}>{children}</MessageContextProvider>;
};

// Local copy of the flag string. placement-contexts gates page-load order on the same
// name and does not export it. Keep in sync with the test's NARROW_EXPOSURE_V2_GATE.
const NARROW_EXPOSURE_V2_GATE = 'post-office_enable_narrow-exposure-v2';

const createFrontendPlacementMessageRunner: CreateFrontendPlacementMessageRunner =
	({ messageTemplates, ...config }) =>
	({
		messageTemplateId,
		messageInstanceId,
		messageCategory,
		inAppNarrowExposureExperiment,
		children,
		...props
	}) => {
		const renderableStage = messageTemplates?.[messageTemplateId];
		const isHeadless = config.headlessMessageTemplateIds?.includes(messageTemplateId);

		const placementLocation: PlacementLocation = {
			messageTemplateId,
			messageInstanceId,
			placementLevel: 'Message',
			stageName: config?.stageName,
			placementId: config.placementId,
		};

		useEffect(() => {
			if (isHeadless && renderableStage) {
				// eslint-disable-next-line no-console
				console.error(
					new PlacementMessageError({
						...placementLocation,
						...config,
					}),
					'Misconfiguration: message is registered as both headless and has a MessageFrontend renderer. Remove it from headlessMessageTemplateIds or remove the MessageFrontend export.',
				);
			}

			if (!isHeadless && !renderableStage) {
				// eslint-disable-next-line no-console
				console.error(
					new PlacementMessageError({ ...placementLocation, ...config }),
					'Misconfiguration: message is not registered as headless but has no MessageFrontend renderer. Register it as a headless message by reinstalling or add a MessageFrontend export.',
				);
			}
		}, [isHeadless, placementLocation, renderableStage, config]);

		const componentPropsStable = useStableObject(props);
		const messageProps = props as {
			triggerType?: TriggerType;
			context?: { triggerType?: TriggerType };
		};
		const triggerType = messageProps.triggerType ?? messageProps.context?.triggerType;
		const hasTrigger = isClientTriggeredMessage(triggerType);
		const isNarrowExposureV2Enabled = FeatureGates.checkGate(NARROW_EXPOSURE_V2_GATE);
		const narrowExposureConfig = isNarrowExposureV2Enabled
			? inAppNarrowExposureExperiment
			: undefined;

		const { waitForTrigger } = useWaitForTrigger({
			hasTrigger,
			messageSuccessComponent: undefined,
			messageInstanceId,
			messageTemplateId,
			messageCategory,
			inAppNarrowExposureExperiment: narrowExposureConfig,
		});

		if (inAppNarrowExposureExperiment && !isNarrowExposureV2Enabled) {
			return null;
		}

		if (!renderableStage) {
			return null;
		}

		try {
			renderableStage.validate({ messageTemplateId, children, ...props });
			const { Component } = renderableStage;

			return (
				<MessageRendererProvider
					{...componentPropsStable}
					messageTemplateId={messageTemplateId}
					messageInstanceId={messageInstanceId}
					messageCategory={messageCategory}
					isUFOEnabled={false}
					componentProps={componentPropsStable}
					waitForTrigger={waitForTrigger}
					inAppNarrowExposureExperiment={narrowExposureConfig}
				>
					<MessageCategoryProvider messageCategory={messageCategory}>
						{!waitForTrigger && <MessageViewed />}
						<Component {...props} waitForTrigger={waitForTrigger}>
							{children}
						</Component>
					</MessageCategoryProvider>
				</MessageRendererProvider>
			);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(createPlacementMessageError(error));

			return null;
		}
	};

const createFrontendPlacementMessageTemplateRouter = <T extends AnyFrontendStages>(
	config: CreateFrontendPlacementMessageExecuteConfig<T>,
): T => {
	const messageTemplateLookup = createMessageTemplateByStageLookup(config);

	// The pure lookup transformation preserves stage names and installs a renderer for each one.
	return Object.entries(messageTemplateLookup).reduce(
		(base, [stageName, messageTemplates]) => ({
			...base,
			[stageName]: createFrontendPlacementMessageRunner({
				messageTemplates,
				stageName,
				placementId: config.placementId,
				headlessMessageTemplateIds: config.headlessMessageTemplateIds,
			}),
		}),
		{},
	) as T;
};

const createMessageTemplateByStageLookup = <T extends AnyValidatedFrontendStages>(config: {
	messageTemplates: Array<PlacementMessage<T>>;
}): Record<string, AnyValidatedFrontendStages> => {
	const result: Record<string, AnyValidatedFrontendStages> = {};

	config.messageTemplates.forEach(([messageTemplateId, stage]) => {
		Object.entries(stage).forEach(([stageName, stageExecutable]) => {
			if (!result[stageName]) {
				result[stageName] = {};
			}
			result[stageName][messageTemplateId] = stageExecutable;
		});
	});

	return result;
};

export { createCustomRendererFrontend } from './create-custom-renderer-frontend';
export { CUSTOM_RENDERERS_GATE } from './custom-renderer-feature-gate';
